import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { MapPin } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const LocationModal = ({ isOpen, onClose, onSubmit, product }) => {
  const [location, setLocation] = useState('');
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (product && isOpen) {
      setLocation(product.location || '');
    }
  }, [product, isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await onSubmit({
        location: location.trim() || null
      });
      onClose();
    } catch (error) {
      console.error('Error updating location:', error);
      toast({
        title: "Error",
        description: "Failed to update location. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[400px]">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold flex items-center gap-2">
            <MapPin className="w-5 h-5" />
            {product?.location ? 'Change Location' : 'Add Location'}
          </DialogTitle>
          <p className="text-sm text-slate-600 dark:text-slate-400">
            {product?.title}
          </p>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Current Location Display (if exists) */}
          {product?.location && (
            <div className="space-y-3">
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <MapPin className="w-5 h-5 text-blue-600" />
                  <Label className="text-sm font-medium text-blue-900">Current Location</Label>
                </div>
                <p className="text-blue-800 font-medium">{product.location}</p>
              </div>
            </div>
          )}

          {/* Location Input */}
          <div className="space-y-2">
            <Label htmlFor="location">
              {product?.location ? 'New Location' : 'Location'}
            </Label>
            <Input
              id="location"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="Enter product location (e.g., Building A, Floor 3, Storage Room 101)"
              className="w-full"
            />
            <p className="text-xs text-slate-500 dark:text-slate-400">
              {product?.location 
                ? "Enter a new location to replace the current one, or leave blank to remove location"
                : "Optional: Specify where this product is physically located"
              }
            </p>
          </div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-3 pt-6 justify-center">
            {product?.location && (
              <Button
                type="button"
                variant="outline"
                onClick={async () => {
                  setLocation('');
                  try {
                    await onSubmit({ location: null });
                    onClose();
                  } catch (error) {
                    console.error('Error removing location:', error);
                    toast({
                      title: "Error",
                      description: "Failed to remove location. Please try again.",
                      variant: "destructive",
                    });
                  }
                }}
                disabled={loading}
                className="text-red-600 hover:text-red-700 border-red-200 hover:border-red-300 order-last sm:order-first"
              >
                Remove Location
              </Button>
            )}
            <div className="flex gap-3 justify-center sm:justify-end">
              <Button type="button" variant="outline" onClick={onClose}>
                {product?.location ? 'Cancel' : 'Close'}
              </Button>
              <Button
                type="submit"
                disabled={loading || (!location.trim() && !product?.location)}
                className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
              >
                {loading ? 'Saving...' : (product?.location ? 'Update Location' : 'Save Location')}
              </Button>
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default LocationModal;